import * as THREE from 'three'

type DustSurface = 'stone' | 'sand' | 'crate' | 'wood' | 'metal'

const SIZE = 512
const cache = new Map<string, THREE.DataTexture>()

export function makeProceduralDustTexture(name: string): THREE.Texture | null {
  const surface = classifyDustSurface(name.toLowerCase())
  if (!surface) return null

  const key = surface
  const cached = cache.get(key)
  if (cached) return cached

  const rgba = new Uint8Array(SIZE * SIZE * 4)
  const seed = hashString(surface)

  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const uvx = x / SIZE
      const uvy = y / SIZE
      const [r, g, b] =
        surface === 'stone'
          ? stonePixel(uvx, uvy, seed)
          : surface === 'sand'
            ? sandPixel(uvx, uvy, seed)
            : surface === 'crate'
              ? cratePixel(uvx, uvy, seed)
              : surface === 'wood'
                ? woodPixel(uvx, uvy, seed)
                : metalPixel(uvx, uvy, seed)

      const o = (y * SIZE + x) * 4
      rgba[o] = clampByte(r)
      rgba[o + 1] = clampByte(g)
      rgba[o + 2] = clampByte(b)
      rgba[o + 3] = 255
    }
  }

  const tex = new THREE.DataTexture(rgba, SIZE, SIZE, THREE.RGBAFormat)
  tex.name = `procedural-${surface}`
  tex.colorSpace = THREE.SRGBColorSpace
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  tex.magFilter = THREE.LinearFilter
  tex.minFilter = THREE.LinearMipmapLinearFilter
  tex.generateMipmaps = true
  tex.anisotropy = 16
  tex.needsUpdate = true
  cache.set(key, tex)
  return tex
}

function classifyDustSurface(name: string): DustSurface | null {
  if (
    name === 'black' ||
    name === 'white' ||
    name === 'generic011' ||
    name.includes('lgt') ||
    name.includes('credit')
  ) {
    return null
  }

  if (
    name.includes('crte') ||
    name.includes('crt')
  ) {
    return 'crate'
  }

  if (
    name.includes('door') ||
    name.includes('wndw')
  ) {
    return 'wood'
  }

  if (name.includes('mltry')) return 'metal'

  if (name.includes('road') || /-?\d?sand$/i.test(name)) return 'sand'

  if (
    name.includes('wall') ||
    name.includes('crete') ||
    name.includes('rock') ||
    name.includes('trim')
  ) {
    return 'stone'
  }

  return null
}

function stonePixel(u: number, v: number, seed: number): [number, number, number] {
  const blocksX = 4
  const blocksY = 7
  const row = Math.floor(v * blocksY)
  const stagger = row % 2 === 0 ? 0 : 0.5 / blocksX
  const warp = fbm(u * 5 + seed, v * 5 - seed) * 0.035
  const bu = fract((u + warp) * blocksX + stagger)
  const bv = fract((v - warp * 0.7) * blocksY)
  const mortar = 1 - smoothstep(0.012, 0.04, Math.min(bu, bv, 1 - bu, 1 - bv))
  const chip =
    fbm(u * 18 + seed * 0.17, v * 18 - seed * 0.13) * 0.5 +
    fbm(u * 62 - seed * 0.04, v * 62 + seed * 0.09) * 0.5
  const ridge = Math.abs(fbm(u * 9 + row, v * 22 + seed) - 0.5)
  const shade = 0.94 + chip * 0.2 - ridge * 0.12 - mortar * 0.18
  const warm = fbm(u * 3 + seed, v * 4 - seed)

  return [
    (184 + warm * 38) * shade,
    (158 + warm * 28) * shade,
    (116 + warm * 18) * shade,
  ]
}

function sandPixel(u: number, v: number, seed: number): [number, number, number] {
  const base = fbm(u * 18 + seed, v * 18 - seed)
  const fine = noise(u * 180 - seed * 0.2, v * 180 + seed * 0.33)
  const pebble = smoothstep(0.82, 0.98, noise(u * 95 + seed, v * 95 - seed))
  const rut = Math.sin((u * 2.2 + fbm(u * 4, v * 7) * 0.6) * Math.PI * 2)
  const shade = 0.98 + base * 0.18 + (fine - 0.5) * 0.1 - pebble * 0.12 + rut * 0.02

  return [208 * shade, 166 * shade, 108 * shade]
}

function cratePixel(u: number, v: number, seed: number): [number, number, number] {
  const border = Math.min(u, v, 1 - u, 1 - v)
  const frame = 1 - smoothstep(0.055, 0.145, border)
  const inner = smoothstep(0.09, 0.16, border)
  const bevelHi =
    smoothstep(0.03, 0.13, u) * smoothstep(0.03, 0.13, v) * 0.12
  const bevelLo =
    (1 - smoothstep(0.86, 0.98, u) * smoothstep(0.86, 0.98, v)) * 0.04

  const panelU = THREE.MathUtils.clamp((u - 0.14) / 0.72, 0, 1)
  const panelV = THREE.MathUtils.clamp((v - 0.14) / 0.72, 0, 1)
  const plank = fract(panelU * 5)
  const plankSeam =
    inner *
    (1 - smoothstep(0.018, 0.05, Math.min(plank, 1 - plank)))
  const panelInset =
    inner *
    (1 - smoothstep(0.02, 0.09, Math.min(panelU, panelV, 1 - panelU, 1 - panelV)))

  const grainWarp = fbm(u * 8 + seed, v * 5 - seed) * 0.06
  const grain = Math.sin((v + grainWarp) * 72 + fbm(u * 18, v * 18) * 5) * 0.5 + 0.5
  const dirt = fbm(u * 15 - seed, v * 15 + seed)
  const knot = smoothstep(0.72, 0.95, fbm(u * 22 + seed * 4, v * 22 - seed * 3))
  const verticalAge = smoothstep(0.2, 1.0, v) * 0.08

  const frameShade = 0.78 + grain * 0.18 + bevelHi - bevelLo - dirt * 0.08
  const panelShade =
    0.66 +
    grain * 0.18 -
    plankSeam * 0.22 -
    panelInset * 0.12 -
    knot * 0.14 -
    verticalAge
  const shade = frame > 0.45 ? frameShade : panelShade
  const outline = Math.max(frame, plankSeam * 0.85, panelInset * 0.65)
  const colorWear = smoothstep(0.55, 0.95, dirt) * 0.06

  return [
    (126 + colorWear * 180) * shade - outline * 28,
    (82 + colorWear * 120) * shade - outline * 20,
    (50 + colorWear * 70) * shade - outline * 12,
  ]
}

function woodPixel(u: number, v: number, seed: number): [number, number, number] {
  const planks = 5
  const plank = fract(u * planks)
  const seam = 1 - smoothstep(0.012, 0.035, Math.min(plank, 1 - plank))
  const grainCoord = v * 28 + fbm(u * 12 + seed, v * 8 - seed) * 4
  const grain = Math.sin(grainCoord) * 0.5 + 0.5
  const knots = smoothstep(0.76, 0.96, fbm(u * 16 - seed, v * 16 + seed))
  const shade = 0.78 + grain * 0.2 - seam * 0.18 - knots * 0.16
  const edgeWear = smoothstep(0.42, 0.5, Math.abs(plank - 0.5)) * 0.06

  return [
    (152 + edgeWear * 210) * shade,
    (101 + edgeWear * 155) * shade,
    (59 + edgeWear * 105) * shade,
  ]
}

function metalPixel(u: number, v: number, seed: number): [number, number, number] {
  const panelX = 1 - smoothstep(0.01, 0.025, Math.min(fract(u * 3), 1 - fract(u * 3)))
  const panelY = 1 - smoothstep(0.01, 0.025, Math.min(fract(v * 3), 1 - fract(v * 3)))
  const scratch = Math.pow(noise(u * 220 + seed, v * 12 - seed), 7)
  const oxidation = fbm(u * 9 - seed, v * 9 + seed)
  const shade = 0.82 + oxidation * 0.14 + scratch * 0.22 - (panelX + panelY) * 0.1

  return [108 * shade, 118 * shade, 101 * shade]
}

function hashString(s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return (h >>> 0) / 0xffffffff
}

function hash(x: number, y: number): number {
  const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453123
  return n - Math.floor(n)
}

function noise(x: number, y: number): number {
  const ix = Math.floor(x)
  const iy = Math.floor(y)
  const fx = x - ix
  const fy = y - iy
  const ux = fx * fx * (3 - 2 * fx)
  const uy = fy * fy * (3 - 2 * fy)
  const a = hash(ix, iy)
  const b = hash(ix + 1, iy)
  const c = hash(ix, iy + 1)
  const d = hash(ix + 1, iy + 1)
  return lerp(lerp(a, b, ux), lerp(c, d, ux), uy)
}

function fbm(x: number, y: number): number {
  let v = 0
  let a = 0.5
  for (let i = 0; i < 5; i++) {
    v += noise(x, y) * a
    const nx = x * 1.72 + y * 1.11 + 19.1
    const ny = y * 1.72 - x * 1.11 - 7.4
    x = nx
    y = ny
    a *= 0.5
  }
  return v
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)))
  return t * t * (3 - 2 * t)
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

function fract(v: number): number {
  return v - Math.floor(v)
}

function clampByte(v: number): number {
  return Math.max(0, Math.min(255, Math.round(v)))
}
